import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FormW9Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form W-9 Instructions 2025: How to Fill Out IRS Form W-9 "
          description="Learn how to fill out Form W-9, understand TIN requirements, avoid 
common mistakes, and stay IRS compliant with TaxZerone's complete guide."
          keywords=""
          canonical="resources/essential-forms/form-w-9-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
            mb: 2,
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> Form W-9</span>
          </span>{' '}
          Instructions: Request{' '}
          <Hidden smDown>
            {' '}
            <br />
          </Hidden>{' '}
          TINs and stay IRS Compliant
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w9instructions`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs currentPageLabel="Form W-9 Instructions" />

          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/excise-tax/form-2290-instructions/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-21T09:00:00-05:00"
                      >
                        August 21, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-21T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        August 21, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form W-9 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}>Form W-9</span> is
                        used to collect a payee's{' '}
                        <span style={{ fontWeight: 600 }}>
                          Taxpayer Identification Number (TIN)
                        </span>{' '}
                        and tax certification for IRS information reporting.
                      </span>
                    </li>

                    <li>
                      <span>
                        Businesses request Form W-9 from{' '}
                        <span style={{ fontWeight: 600 }}>
                          independent contractors, freelancers, vendors, and
                          other U.S. payees
                        </span>{' '}
                        before making reportable payments.
                      </span>
                    </li>

                    <li>
                      <span>
                        <span style={{ fontWeight: 600 }}>
                          Form W-9 is not filed with the IRS
                        </span>
                        . It is kept by the requester and used to prepare Forms{' '}
                        <span style={{ fontWeight: 600 }}>1099</span> and other
                        information returns.
                      </span>
                    </li>

                    <li>
                      <span>
                        This guide explains how to complete each section of Form
                        W-9, including{' '}
                        <span style={{ fontWeight: 600 }}>
                          name, tax classification, TIN, exemptions, and
                          certification
                        </span>
                        .
                      </span>
                    </li>

                    <li>
                      <span>
                        Providing an incorrect TIN or failing to submit Form W-9
                        may result in{' '}
                        <span style={{ fontWeight: 600 }}>
                          24% backup withholding
                        </span>{' '}
                        and IRS penalties.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form W-9, Request for Taxpayer Identification Number (TIN) and
                  Certification, is a U.S. Internal Revenue Service (IRS) tax
                  form that businesses use to obtain information regarding the
                  taxpayer identification numbers of their vendors, contractors,
                  freelancers, or any other payees. Understanding what a w-9
                  form is, how to fill it out correctly, and when it is required
                  can help ensure accurate tax reporting and IRS compliance.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you are an independent contractor{' '}
                  <Link
                    href="/request-form-w9-online-free/"
                    sx={{ color: '#0202ff' }}
                  >
                    filling a w9 form
                  </Link>{' '}
                  or a company requesting taxpayer identification numbers, then
                  we have covered all your needs on W-9 form 2025.
                </Typography>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatnew" sx={{ color: '#0202ff' }}>
                          What's new in Form W-9?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is a W-9 Form?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When is w-9 used for?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who needs to complete Form W-9?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#purpose" sx={{ color: '#0202ff' }}>
                          What is the purpose of the W-9 form?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to fill out the W-9 form?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Common W-9 Mistakes
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatnew"
                >
                  What's new in Form W-9?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    The IRS created a
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      new exempt recipient category{' '}
                    </span>{' '}
                    for certain businesses that deal with digital assets.
                  </li>
                  <li>
                    A
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      digital asset broker{' '}
                    </span>{' '}
                    (such as some cryptocurrency exchanges or platforms) may not
                    need to be reported in the same way as regular customers.
                  </li>
                  <li>
                    To make this easier,
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Part II (Certification){' '}
                    </span>{' '}
                    of Form W-9 now includes a
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      new checkbox.{' '}
                    </span>{' '}
                  </li>
                  <li>
                    By checking this box, one broker can certify to another
                    broker that it is a
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      U.S. digital asset broker{' '}
                    </span>{' '}
                    and qualifies as an
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      exempt recipient{' '}
                    </span>{' '}
                    under IRS regulations.
                  </li>
                  <li>
                    This helps brokers determine whether they need to issue
                    information returns (such as Form 1099-DA) for digital asset
                    transactions.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is a W-9 Form?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  An individual or entity (the{' '}
                  <span style={{ fontWeight: 600 }}> Form W-9 requester</span> )
                  that is required to file an information return with the IRS
                  provides <span style={{ fontWeight: 600 }}> Form W-9</span> to
                  the recipient to obtain the recipient's correct Taxpayer
                  Identification Number (TIN). The requester uses this
                  information to accurately report payments made to the
                  recipient or any other amount required to be reported on an
                  IRS information return
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Simplify W-9 collection and IRS Reporting with TaxZerone
                      </Typography>
                      <Typography variant="body2">
                        Collect taxpayer identification numbers (TINs), manage
                        payee information, and streamline information return
                        filing from one secure platform
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w9instructions`}
                      >
                        E-file now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="when"
                >
                  When is W-9 used for?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Form W-9</span> is used when
                  a business or organization needs to collect a person's or
                  business's{' '}
                  <span style={{ fontWeight: 600 }}>
                    Taxpayer Identification Number (TIN)
                  </span>{' '}
                  and tax classification so it can accurately report certain
                  payments to the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Common situations where a W-9 is used include:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Independent contractor payments
                    </span>{' '}
                    - Before paying a freelancer or contractor who may receive a
                    Form 1099-NEC.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>Vendor onboarding</span> -
                    Businesses collect W-9s from suppliers or service providers
                    for future tax reporting.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>Rent payments</span> -
                    When rent payments must be reported on a Form
                    1099-MISC.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Interest and dividend payments
                    </span>{' '}
                    - Banks and financial institutions use W-9 information to
                    prepare forms such as Form 1099-INT and Form 1099-DIV.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Other reportable payments
                    </span>{' '}
                    - Such as royalties, prizes, awards, medical and healthcare
                    payments, or attorney payments that require IRS information
                    reporting.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who Needs to Complete Form W-9?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  A business or individual will typically ask recipient to
                  complete a W 9 if recipient is:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    An independent contractor or freelancer being paid for
                    services
                  </li>
                  <li>
                    A vendor or supplier receiving payments from a business
                  </li>
                  <li>
                    Receiving interest, dividends, or other reportable income
                  </li>
                  <li>
                    A landlord receiving rental income through a property
                    management company
                  </li>
                  <li>
                    An LLC, partnership, or sole proprietor entering into a
                    business relationship involving reportable payments
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If you're a regular employee receiving a{' '}
                  <Link
                    href="/request-form-w9-online-free/"
                    sx={{ color: '#0202ff' }}
                  >
                    W-2
                  </Link>{' '}
                  , you generally won't need to complete a W9 for your employer.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="purpose"
                >
                  What is the purpose of the W-9 form?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form W-9 is used by businesses and other organizations to
                  obtain a recipient correct Taxpayer Identification Number
                  (TIN), which may be a Social Security Number (SSN) or an
                  Employer Identification Number (EIN). The information obtained
                  from this form assists payers in reporting payments made to
                  recipient to the Internal Revenue Service (IRS).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Information obtained from W 9 is typically used to file
                  several types of information returns, such as:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <Typography variant="body1">
                      <Link
                        href="/resources/information-returns/form-1099-int-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-INT
                      </Link>{' '}
                      (interest earned or paid).
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <Link
                        href="/resources/information-returns/form-1099-div-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-DIV
                      </Link>{' '}
                      (dividends, including those from stocks or mutual funds).
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <Link
                        href="/resources/information-returns/form-1099-da-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-DA
                      </Link>{' '}
                      (digital assets).
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <Link
                        href="/resources/information-returns/form-1099-misc-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-MISC
                      </Link>{' '}
                      (various types of income, prizes, awards, or gross
                      proceeds).
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <Link
                        href="/resources/information-returns/form-1099-nec-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-NEC
                      </Link>{' '}
                      (nonemployee compensation).
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <Link
                        href="/resources/information-returns/form-1099-b-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-B
                      </Link>{' '}
                      (stock or mutual fund sales and certain other transactions
                      by brokers).
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <Link
                        href="/resources/information-returns/form-1099-s-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-S
                      </Link>{' '}
                      (proceeds from real estate transactions).
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <Link
                        href="/resources/information-returns/form-1099-k-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-K
                      </Link>{' '}
                      (merchant card and third-party network transactions).
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <Link
                        href="/resources/information-returns/form-1098-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1098
                      </Link>{' '}
                      (home mortgage interest),{' '}
                      <Link
                        href="/resources/information-returns/form-1098-e-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        1098-E
                      </Link>{' '}
                      (student loan interest), and{' '}
                      <Link
                        href="/resources/information-returns/form-1098-t-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        1098-T
                      </Link>{' '}
                      (tuition).
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <Link
                        href="/resources/information-returns/form-1099-c-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-C
                      </Link>{' '}
                      (canceled debt).
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <Link
                        href="/resources/information-returns/form-1099-a-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-A
                      </Link>{' '}
                      (acquisition or abandonment of secured property).
                    </Typography>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Signing the form W9, a payee agrees that the information they
                  provided about their TIN is accurate and his tax status is
                  valid
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Backup withholding?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> Backup withholding</span>{' '}
                  is a federal tax withholding requirement where the IRS
                  requires the payer to withhold{' '}
                  <span style={{ fontWeight: 600 }}> 24%</span> of certain
                  payments and send it directly to the IRS instead of instead of
                  full amount to the recipient.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Backup withholding may apply to payments such as:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <Typography variant="body1">Interest income</Typography>
                  </li>

                  <li>
                    <Typography variant="body1">Dividends</Typography>
                  </li>

                  <li>
                    <Typography variant="body1">Tax-exempt interest</Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      Nonemployee compensation
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">Rents and royalties</Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      Broker and barter exchange transactions
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      Payment card and third-party network transactions
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      Certain payments from fishing boat operators
                    </Typography>
                  </li>
                </Box>

                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note :</b> Real estate transactions are generally{' '}
                  <span style={{ fontWeight: 600 }}>not subject</span> to backup
                  withholding.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="how"
                >
                  How to Complete Form W-9: Step-by-Step
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Step-by-step instructions explains what recipients will do to{' '}
                  <Link
                    href="/request-form-w9-online-free/"
                    sx={{ color: '#0202ff' }}
                  >
                    fill out W-9 form
                  </Link>{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                >
                  Line 1: Name of the Entity or individual.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w9-line-1-img.webp" />
                  <source srcSet="/assets/images/w9-line-1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/w9-line-1-img.webp"
                    loading="lazy"
                    alt="Form W-9-line-1-name of the entity or individual"
                    width={757}
                    height={35}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name that matches the Taxpayer Identification Number
                  (TIN) provided on Form W-9. This name must be the same as the
                  name shown on your federal tax return.
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Individuals:</span>{' '}
                      Enter the name shown on your tax return. If you have
                      changed your last name without informing the Social
                      Security Administration (SSA) of the name change, enter
                      your first name, the last name as shown on your social
                      security card, and your new last name.
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Sole Proprietors:</span>{' '}
                      Enter your individual name as shown on your Form 1040 on
                      Line 1. Enter your business, trade, or “doing business as”
                      (DBA) name on Line 2.
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>
                        Partnership, C corporation, S corporation, or LLC, other
                        than a disregarded entity:
                      </span>{' '}
                      Enter the entity’s name as shown on the entity’s tax
                      return on Line 1 and any business, trade, or DBA name on
                      Line 2.
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>Other entities:</span>{' '}
                      Enter your name as shown on required U.S. federal tax
                      documents on Line 1. This name should match the name shown
                      on the charter or other legal document creating the
                      entity. Enter any business, trade, or DBA name on Line 2.
                    </Typography>
                  </li>

                  <li>
                    <Typography variant="body1">
                      <span style={{ fontWeight: 600 }}>
                        Disregarded entity:
                      </span>{' '}
                      Enter the owner’s name on Line 1. The name of the owner
                      entered on Line 1 should never be a disregarded entity.
                      The name on Line 1 should be the name shown on the income
                      tax return on which the income should be reported.
                    </Typography>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                >
                  Line 2: Business Name
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w9-line-2-img.webp" />
                  <source srcSet="/assets/images/w9-line-2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/w9-line-2-img.webp"
                    loading="lazy"
                    alt="Form W-9-line-2- business name"
                    width={757}
                    height={30}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the business name, trade name, DBA name, or disregarded
                  entity name, enter name on this line (if different).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                >
                  Line 3a Federal Tax Classification
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w9-line-3a-img.webp" />
                  <source srcSet="/assets/images/w9-line-3a-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/w9-line-3a-img.webp"
                    loading="lazy"
                    alt="Form W-9-line-3a-federal tax classification "
                    width={757}
                    height={180}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select the box that matches the federal tax classification of
                  the individual or entity listed on Line 1. Only one box should
                  be checked.
                </Typography>

                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="Entity Classification Table">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            IF the entity/individual on Line 1 is a(n)...
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            THEN check the box for...
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                            }}
                          >
                            Corporation
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            Corporation
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                            }}
                          >
                            Individual or Sole proprietorship
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            Individual/sole proprietor.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                            }}
                          >
                            LLC classified as a partnership for U .S. federal
                            tax purposes or LLC t hat has filed Form 8832 o
                            corporationr 2553
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            Limited liability company and enter appropriate tax
                            tax classification: P = Partnership, C = C
                            corporation, or S = S corporation.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                            }}
                          >
                            Partnership
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            Partnership.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              verticalAlign: 'top',
                            }}
                          >
                            Trust/estate
                          </TableCell>
                          <TableCell sx={{ verticalAlign: 'top' }}>
                            Trust/estate.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                >
                  Line 3b: Foreign Partners, Owners, or Beneficiaries{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w9-line-3b-img.webp" />
                  <source srcSet="/assets/images/w9-line-3b-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/w9-line-3b-img.webp"
                    loading="lazy"
                    alt="Form W-9-line-3b-foreign partners, owners, or beneficiaries "
                    width={757}
                    height={70}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if you are a partnership (including an LLC clas
                  s ified as a partnership for U.S. federal tax purposes),
                  trust, or esta t e that has any foreign partners, owners, or
                  beneficiaries, and you are providing this form to a
                  partnership, trust, or estate, in which you have an ownership
                  interest.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must check the box on line 3b{' '}
                </Typography>

                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    <Typography variant="body1">
                      if you receive a Form W-8 (or documentary evidence) from
                      any partner, owner, or beneficiary establishing foreign
                      status or
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      if you receive a Form W-9 from any partner, owner, or
                      beneficiary, check the box on line 3b.
                    </Typography>
                  </li>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                >
                  Line 4: Exemptions{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w9-line-4-img.webp" />
                  <source srcSet="/assets/images/w9-line-4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/w9-line-4-img.webp"
                    loading="lazy"
                    alt="Line 4: Exemptions"
                    width={400}
                    height={310}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you're exempt from backup withholding or FATCA reporting,
                  enter the applicable code(s) on line 4. Most individuals and
                  sole proprietors leave this blank — exemptions generally apply
                  to corporations, tax-exempt organizations, government
                  entities, and financial institutions.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Exempt payee code:
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="Exempt Payee Codes">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Description
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            1
                          </TableCell>
                          <TableCell>
                            Organization exempt under section 501(a), an IRA, or
                            a custodial account under section 403(b)(7) meeting
                            section 401(f)(2) requirements.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            2
                          </TableCell>
                          <TableCell>
                            The United States or any of its agencies or
                            instrumentalities.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            3
                          </TableCell>
                          <TableCell>
                            A state, D.C., a U.S. territory, or their political
                            subdivisions/instrumentalities.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            4
                          </TableCell>
                          <TableCell>
                            A foreign government or its political
                            subdivisions/agencies/instrumentalities.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            5
                          </TableCell>
                          <TableCell>A corporation.</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            6
                          </TableCell>
                          <TableCell>
                            A dealer in securities or commodities required to
                            register in the United States, the District of
                            Columbia, or a U.S. commonwealth or territory.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            7
                          </TableCell>
                          <TableCell>
                            A futures commission merchant registered with the
                            Commodity Futures Trading Commission.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            8
                          </TableCell>
                          <TableCell>A real estate investment trust.</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            9
                          </TableCell>
                          <TableCell>
                            An entity registered at all times during the tax
                            year under the Investment Company Act of 1940.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            10
                          </TableCell>
                          <TableCell>
                            A common trust fund under section 584(a).
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            11
                          </TableCell>
                          <TableCell>
                            A financial institution under section 581.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            12
                          </TableCell>
                          <TableCell>
                            A middleman known in the investment community as a
                            nominee or custodian.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            13
                          </TableCell>
                          <TableCell>
                            A trust exempt from tax under section 664 or
                            described in section 4947.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            14
                          </TableCell>
                          <TableCell>
                            A U.S. digital asset broker (other than a registered
                            investment adviser that is not otherwise an exempt
                            recipient under Regulations section
                            1.6045-1(c)(3)(i)(B)(1) through (11)) that is an
                            exempt recipient under Regulations section 1.6045
                            1(c)(3)(i)(B)(12).
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  FATCA exemption codes:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Most taxpayers with U.S.-based accounts leave this blank.
                  FATCA codes generally apply to specific exempt entities or
                  accounts held outside the U.S. by foreign financial
                  institutions.
                </Typography>

                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="Exempt Payee Codes">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Description
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>A</TableCell>
                          <TableCell>
                            Organization exempt under section 501(a), or an
                            individual retirement plan under section
                            7701(a)(37).
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>B</TableCell>
                          <TableCell>
                            The United States or its agencies/instrumentalities.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>C</TableCell>
                          <TableCell>
                            A state, D.C., a U.S. territory, or their political
                            subdivisions/instrumentalities.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>D</TableCell>
                          <TableCell>
                            A publicly traded corporation per Regulations
                            section 1.1472-1(c)(1)(i).
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>E</TableCell>
                          <TableCell>
                            A corporation in the same expanded affiliated group
                            as a corporation described above.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>F</TableCell>
                          <TableCell>
                            A registered dealer in securities, commodities, or
                            derivative financial instruments.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>G</TableCell>
                          <TableCell>A real estate investment trust.</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>H</TableCell>
                          <TableCell>
                            A regulated investment company under section 851, or
                            an entity registered under the Investment Company
                            Act of 1940.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>I</TableCell>
                          <TableCell>
                            A common trust fund under section 584(a).
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>J</TableCell>
                          <TableCell>A bank under section 581.</TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>K</TableCell>
                          <TableCell>A broker.</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>L</TableCell>
                          <TableCell>
                            A trust exempt under section 664, or described in
                            section 4947(a)(1).
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>M</TableCell>
                          <TableCell>
                            A tax-exempt trust under a section 403(b) or 457(g)
                            plan.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                >
                  Line 5: Address{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w9-line-5-img.webp" />
                  <source srcSet="/assets/images/w9-line-5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/w9-line-5-img.webp"
                    loading="lazy"
                    alt="Form W-9-line-5-address"
                    width={757}
                    height={54}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your address (number, street, and apartment or suite
                  number). This is where the requester of this Form W-9 will
                  mail your{' '}
                  <Link href="/information-returns/" sx={{ color: '#0202ff' }}>
                    information returns
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                >
                  Line 6: City, state, and ZIP Code
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w9-line-6-img.webp" />
                  <source srcSet="/assets/images/w9-line-6-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/w9-line-6-img.webp"
                    loading="lazy"
                    alt="Form W-9- line-6-city, state, and zip code  "
                    width={757}
                    height={50}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your city, state, and ZIP code.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                >
                  Line 7: Account numbers
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w9-line-7-img.webp" />
                  <source srcSet="/assets/images/w9-line-7-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/w9-line-7-img.webp"
                    loading="lazy"
                    alt="Form W-9-line 7- account numbers"
                    width={757}
                    height={50}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This optional field is used to provide account number(s)
                  associated with the account for which you are{' '}
                  <Link
                    href="/request-form-w9-online-free/"
                    sx={{ color: '#0202ff' }}
                  >
                    submitting Form W-9
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                >
                  Part I: Taxpayer Identification Number (TIN)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w9-part-I-TIN-img.webp" />
                  <source srcSet="/assets/images/w9-part-I-TIN-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/w9-part-I-TIN-img.webp"
                    loading="lazy"
                    alt="Form w-9-part-1-taxpayer identification number "
                    width={757}
                    height={170}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your TIN in the appropriate box. If you are a resident
                  alien and you do not have, and are not eligible to get, an
                  SSN, your TIN is your IRS ITIN. Enter it in the entry space
                  for the social security number{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    <Typography variant="body1">
                      If you are a sole proprietor and you have an EIN, you may
                      enter your SSN or EIN.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      If you are a single-member LLC that is disregarded as an
                      entity separate from its owner, enter the owner’s SSN (or
                      EIN, if the owner has one).
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body1">
                      If the LLC is classified as a corporation or partnership,
                      enter the entity’s EIN
                    </Typography>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                >
                  Part II: Certification
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w9-part-II-centification-img.webp" />
                  <source srcSet="/assets/images/w9-part-II-centification-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/w9-part-II-centification-img.webp"
                    loading="lazy"
                    alt="Form w-9-part-1-taxpayer identification number "
                    width={757}
                    height={220}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  By signing and dating Form W-9, you certify that:
                </Typography>

                <List sx={{ listStyleType: 'decimal', pl: 4 }}>
                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    Your TIN is correct (or you're waiting for one to be
                    issued).
                  </ListItem>

                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    You're not subject to backup withholding, unless the IRS has
                    notified you otherwise.
                  </ListItem>

                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    You're a U.S. citizen, resident alien, or other U.S. person.
                  </ListItem>

                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    Any FATCA exemption code you entered is accurate.
                  </ListItem>

                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    If you are a{' '}
                    <span style={{ fontWeight: 600 }}>
                      U.S. digital asset broker
                    </span>{' '}
                    (such as certain cryptocurrency exchanges or digital asset
                    trading platforms), you certify that you qualify for an
                    exemption from certain information reporting requirements.
                  </ListItem>
                </List>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  This certification lets the requester confirm your information
                  is valid for reporting purposes. In most cases — such as
                  submitting a w 9 to a client for 1099 purposes — you should
                  sign and date the form before sending it.
                </Typography>
                <Alert
                  icon={<InfoOutlinedIcon />}
                  severity="info"
                  sx={{ mt: 2, mb: 2 }}
                >
                  <b>Remember:</b> Form W9 goes directly to the requester, not
                  the IRS.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                    scrollMarginTop: 83,
                  }}
                >
                  W-9 vs. 1099
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These two forms serve different purposes and go to different
                  places:
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table aria-label="Form W-9 vs Form 1099">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell sx={{ fontWeight: 600, width: '26%' }} />
                          <TableCell sx={{ fontWeight: 600 }}>
                            Form W-9
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Form 1099
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Purpose
                          </TableCell>
                          <TableCell>Collects payee's TIN.</TableCell>
                          <TableCell>
                            Reports payments made to a payee during the tax
                            year.
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Who completes it
                          </TableCell>
                          <TableCell>
                            The payee (contractor, vendor, freelancer, etc.)
                          </TableCell>
                          <TableCell>
                            The payer (business or organization) and individual.
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            IRS filing
                          </TableCell>
                          <TableCell>
                            Generally, not filed with the IRS.
                          </TableCell>
                          <TableCell>Filed with the IRS.</TableCell>
                        </TableRow>

                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            Recipient
                          </TableCell>
                          <TableCell>Provided to the requester.</TableCell>
                          <TableCell>Sent to the IRS and the payee.</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="common"
                >
                  Common W-9 mistakes to Avoid
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <List sx={{ listStyleType: 'disc', pl: 4 }}>
                    <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                      <span style={{ fontWeight: 600 }}>
                        Name and TIN Mismatch:
                      </span>{' '}
                      The name entered on Line 1 and the Taxpayer Identification
                      Number (TIN) provided in Part I must match IRS records.
                      Mismatched information may result in backup withholding or
                      processing issues.
                    </ListItem>

                    <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                      <span style={{ fontWeight: 600 }}>
                        Using the Wrong Name for Sole Proprietors and
                        Single-Member LLCs:
                      </span>{' '}
                      Sole proprietors and single-member LLCs that are
                      disregarded entities should enter the owner's name on Line
                      1 rather than the business or LLC name.
                    </ListItem>

                    <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                      <span style={{ fontWeight: 600 }}>
                        Incorrect Tax Classification on Line 3a:
                      </span>{' '}
                      Disregarded entities should select the tax classification
                      that applies to the owner, rather than choosing “LLC” as
                      the federal tax classification.
                    </ListItem>

                    <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                      <span style={{ fontWeight: 600 }}>
                        Failing to Complete Line 3b:
                      </span>{' '}
                      Certain flow-through entities with foreign owners are
                      required to check this box. Leaving it blank when
                      applicable may lead to incomplete reporting and potential
                      IRS penalties.
                    </ListItem>

                    <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                      <span style={{ fontWeight: 600 }}>
                        Entering Exemption Codes Without Qualifying:
                      </span>{' '}
                      Most individuals are not exempt from reporting
                      requirements and should leave Line 4 blank unless they
                      meet the IRS exemption criteria.
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 2,
                    scrollMarginTop: 83,
                  }}
                  id="faq"
                >
                  Frequently Asked Questions (FAQs)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  1. Who needs a W-9?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Recipient may be asked to complete a Form W-9 if you are an
                  independent contractor, freelancer, consultant, vendor, sole
                  proprietor, LLC, partnership, or other payee receiving
                  reportable payments from a business or organization.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  2. Is form W-9 filed with IRS?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  No. Form W-9 is generally not filed with the IRS. Instead, it
                  is completed by the payee and provided directly to the
                  requester, who retains it for recordkeeping and information
                  return reporting purposes.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  3. What is the difference between W-9 and W-8?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form W-9 is used by U.S. persons, including U.S. citizens,
                  resident aliens, and U.S. entities. Form W-8 is used by
                  foreign individuals and entities to certify their foreign
                  status and, when applicable, claim tax treaty benefits.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  4. What is Backup Withholding?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Backup withholding is a 24% federal tax withholding that may
                  apply if you do not provide a correct TIN on Form W-9 or are
                  subject to IRS withholding requirements.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Learn more about backup withholding.
                </Typography>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  5. What are the penalties for not filing Form W-9?
                </Typography>

                <List sx={{ listStyleType: 'disc', pl: 4, mb: 2 }}>
                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    <span style={{ fontWeight: 600 }}>
                      Failure to furnish TIN:
                    </span>{' '}
                    If you fail to furnish your correct TIN to a requester, you
                    are subject to a penalty of{' '}
                    <span style={{ fontWeight: 600 }}>$50</span> for each such
                    failure unless your failure is due to reasonable cause and
                    not to willful neglect.
                  </ListItem>

                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    <span style={{ fontWeight: 600 }}>
                      Civil penalty for false information with respect to
                      withholding:
                    </span>{' '}
                    If you make a false statement with no reasonable basis that
                    results in no backup withholding, you are subject to a{' '}
                    <span style={{ fontWeight: 600 }}>$500</span> penalty.
                  </ListItem>

                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    <span style={{ fontWeight: 600 }}>
                      Criminal penalty for falsifying information:
                    </span>{' '}
                    Willfully falsifying certifications or affirmations may
                    subject you to criminal penalties including fines and/or
                    imprisonment.
                  </ListItem>

                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    <span style={{ fontWeight: 600 }}>Misuse of TINs:</span> If
                    the requester discloses or uses TINs in violation of federal
                    law, the requester may be subject to civil and criminal
                    penalties.
                  </ListItem>
                </List>

                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  6. What is FATCA reporting?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The Foreign Account Tax Compliance Act (FATCA) requires a
                  participating foreign financial institution to report all U.S.
                  account holders that are specified U.S. persons. Certain
                  payees are exempt from FATCA reporting.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above information is based on the IRS guidelines.
                        Please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/fw9.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          IRS instructions
                        </Link>{' '}
                        of Form W-9.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Collect W-9 Information and E-File with Confidence
            </Typography>

            <Typography
              variant="body1"
              sx={{ mb: 2, color: '#333333', maxWidth: 700, mx: 'auto' }}
            >
              TaxZerone helps businesses simplify taxpayer information
              collection, prepare IRS information returns, and stay compliant
              throughout the filing season.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w9instructions`}
              >
                E-file now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default FormW9Instructions;
